import {useMemo} from 'react';

import type {PublicBallot, VotingSystem} from '../../../shared/ballotTypes';
import {useNow} from '../../../shared/hooks/useNow';
import {clockOffsetMs, formatClock, secondsLeft} from '../../../shared/live/serverClock';
import {PaperButton} from '../../../shared/sticker/stickers';
import {
    type BallotPicks,
    canSubmit,
    changeDots,
    headerTag,
    submitLabel,
    tapOption,
} from '../ballotState';
import {OptionCard} from './optionCard';

export const BallotStep = ({
    ballot,
    receivedAt,
    picks,
    onPicks,
    onSelfVote,
    onSubmit,
    error,
}: {
    ballot: PublicBallot;
    receivedAt: number;
    picks: BallotPicks;
    onPicks: (next: BallotPicks) => void;
    onSelfVote: () => void;
    onSubmit: () => void;
    error: string | null;
}) => {
    const system = ballot.system as VotingSystem;
    const now = useNow(1000);
    const offset = useMemo(
        () => clockOffsetMs(ballot.server_now, receivedAt),
        [ballot.server_now, receivedAt],
    );
    const selfIds = ballot.me?.self_option_ids ?? [];
    const guard = (optionId: string, apply: () => void) =>
        selfIds.includes(optionId) ? onSelfVote() : apply();

    return (
        <main className="ph-page">
            <div className="ph-stack">
                <div className="ph-header">
                    <p className="ph-tag">{headerTag(system, picks, ballot.dots_per_voter)}</p>
                    {ballot.ends_at && (
                        <span className="ph-clock">
                            {formatClock(secondsLeft(ballot.ends_at, now, offset))}
                        </span>
                    )}
                </div>
                <h1 className="ph-title">{ballot.title}</h1>
                {ballot.show_voters && (
                    <p className="ph-notice">Heads up: votes are public in this one.</p>
                )}
                <div className="ph-options">
                    {ballot.options.map((option) => (
                        <OptionCard
                            key={option.id}
                            option={option}
                            system={system}
                            value={picks[option.id]}
                            onTap={() =>
                                guard(option.id, () => onPicks(tapOption(system, picks, option.id)))
                            }
                            onStep={(delta) =>
                                guard(option.id, () =>
                                    onPicks(
                                        changeDots(picks, option.id, delta, ballot.dots_per_voter),
                                    ),
                                )
                            }
                        />
                    ))}
                </div>
                {system === 'preferential' && (
                    <p className="ph-muted">Tap a number to remove it. Rank as many as you like.</p>
                )}
                {error && (
                    <p className="ph-error" role="alert">
                        {error}
                    </p>
                )}
                <PaperButton
                    className="ph-wide ph-submit"
                    disabled={!canSubmit(system, picks)}
                    onClick={onSubmit}
                >
                    {submitLabel(system, picks, ballot.options)}
                </PaperButton>
            </div>
        </main>
    );
};
